import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useSessionTracking } from './useSessionTracking';

export const useViewCount = (blogPostId: string) => {
  const { user } = useAuth();
  const { sessionKey } = useSessionTracking();
  const [viewCount, setViewCount] = useState(0);
  const [lastViewTime, setLastViewTime] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchViewCount = async () => {
    try {
      const { data, error } = await supabase
        .rpc('get_blog_post_view_count', { post_id: blogPostId });

      if (error) throw error;

      setViewCount(data || 0);
    } catch (error: any) {
      console.error('Error fetching view count:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLastViewTime = async () => {
    // Get the most recent view timestamp for this post by this user/session
    try {
      let query = supabase
        .from('views')
        .select('created_at')
        .eq('blog_post_id', blogPostId)
        .order('created_at', { ascending: false })
        .limit(1);

      // Filter by user_id or session_key
      if (user?.id) {
        query = query.eq('user_id', user.id);
      } else if (sessionKey) {
        query = query.eq('session_key', sessionKey);
      } else {
        // No user or session, can't fetch
        return;
      }

      const { data, error } = await query.maybeSingle();

      if (error) throw error;

      if (data) {
        setLastViewTime(new Date(data.created_at));
      }
    } catch (error: any) {
      console.error('Error fetching last view time:', error);
    }
  };

  const trackView = async (sessionKey?: string) => {
    // Only track if we have either user_id or session_key (RLS requirement)
    if (!user?.id && !sessionKey) {
      console.log('Skipping view tracking - no user_id or session_key');
      return;
    }

    try {
      const { error } = await supabase
        .from('views')
        .insert({
          blog_post_id: blogPostId,
          user_id: user?.id || null,
          session_key: sessionKey || null,
          ip_address: null,
          user_agent: navigator.userAgent,
          association_status: 'pending'
        });

      if (error) throw error;
      
      // Refresh the count after tracking a view
      fetchViewCount();
    } catch (error: any) {
      console.error('Error tracking view:', error);
    }
  };

  useEffect(() => {
    fetchViewCount();
    fetchLastViewTime();
  }, [blogPostId, user?.id, sessionKey]);

  // Subscribe to real-time changes for this blog post's views
  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;
    
    const channel = supabase
      .channel('view-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'views',
          filter: `blog_post_id=eq.${blogPostId}`
        },
        () => {
          fetchViewCount();
        }
      )
      .subscribe((status) => {
        // Silently handle connection errors and fallback to polling
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          // Fallback to polling every 30 seconds if realtime fails
          if (!pollInterval) {
            pollInterval = setInterval(fetchViewCount, 30000);
          }
        }
      });

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
      supabase.removeChannel(channel);
    };
  }, [blogPostId]);

  return {
    viewCount,
    lastViewTime,
    loading,
    trackView,
    refetch: fetchViewCount
  };
};